import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { 
  Button, 
  Tile, 
  Tag, 
  Section,
  Toggle,
  TextArea,
  SkeletonText,
  SkeletonPlaceholder
} from '@carbon/react';
import {
  ArrowLeft,
  UserAvatar,
  Restaurant,
  Wheat,
  Information,
  Checkmark,
  Close,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Warning,
  CalendarHeatMap
} from '@carbon/icons-react';
import PageContainer from '../components/PageContainer';
import axiosInstance from '../utils/axiosInstance';
import './ResidentNutrition.css';

const ResidentNutrition = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDarkMode = theme === 'g100';

  const [expandedSections, setExpandedSections] = useState({
    mealHistory: false,
    specialNotes: false
  });

  const [isViewPanelOpen, setIsViewPanelOpen] = useState(false);
  const [viewingMeal, setViewingMeal] = useState(null);
  const [viewMode, setViewMode] = useState('week'); // 'week' or 'day'
  const [dayViewDate, setDayViewDate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [weekOffset, setWeekOffset] = useState(0);
  const [mealHistory, setMealHistory] = useState([]);
  const [menuData, setMenuData] = useState(null);
  const [error, setError] = useState(null);
  const [resident, setResident] = useState(null);
  const [dietType, setDietType] = useState('');
  const [dietDescription, setDietDescription] = useState('');
  const [restrictions, setRestrictions] = useState([]);
  const [allergens, setAllergens] = useState([]);
  const [specialNotes, setSpecialNotes] = useState([]);

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('sr-Latn-RS', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDateKey = (date) => {
    return `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`;
  };

  const getDayLabel = (date) => {
    if (!date) return '';
    const days = ['Nedelja', 'Ponedeljak', 'Utorak', 'Sreda', 'Četvrtak', 'Petak', 'Subota'];
    const months = ['januar', 'februar', 'mart', 'april', 'maj', 'jun', 'jul', 'avgust', 'septembar', 'oktobar', 'novembar', 'decembar'];
    return `${days[date.getDay()]}, ${date.getDate()}. ${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  const mapMenusToMealHistory = (menus) => {
    if (!Array.isArray(menus)) return [];
    const grouped = {};
    menus.forEach(menu => {
      const start = new Date(menu.startTime);
      const dateKey = formatDateKey(start);
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push({
        type: menu.menuName || 'Obrok',
        time: formatTime(menu.startTime),
        items: menu.mealName || '',
        consumed: false,
        percentage: 0
      });
    });
    return Object.keys(grouped).map(dateKey => ({
      date: dateKey,
      day: getDayLabel(new Date(dateKey.split('.').reverse().join('-'))),
      meals: grouped[dateKey]
    }));
  };

  const fetchMealHistory = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(`/api/DailyMenu/by-resident/${id}`, {
        params: { weekOffset }
      });
      if (response.data.success) {
        const data = response.data.data;
        setMenuData(data);
        setMealHistory(mapMenusToMealHistory(data?.menus));
        setResident(data?.resident || null);
        setDietType(data?.dietType || '');
        setDietDescription(data?.dietDescription || '');
        setRestrictions(data?.restrictions || []);
        setAllergens(data?.allergens || []);
        setSpecialNotes(data?.specialNotes || []);
      }
    } catch (err) {
      console.error('Greška pri učitavanju ishrane:', err);
      setError('Nije moguće učitati ishranu. Pokušajte ponovo.');
      setMealHistory([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMealHistory();
  }, [id, weekOffset]);

  const navigateWeek = (direction) => {
    setWeekOffset(prev => prev + direction);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const openViewPanel = (meal, day) => {
    setViewingMeal({ ...meal, day });
    setIsViewPanelOpen(true);
  };

  const closeViewPanel = () => {
    setIsViewPanelOpen(false);
    setViewingMeal(null);
  };

  const navigateDay = (direction) => {
    if (dayViewDate) {
      const newDate = new Date(dayViewDate);
      newDate.setDate(newDate.getDate() + direction);
      setDayViewDate(newDate);
    }
  };

  const goToToday = () => {
    setDayViewDate(new Date());
  };

  const getDayMeals = (date) => {
    if (!date) return [];
    const dateStr = formatDateKey(date);
    const day = mealHistory.find(d => d.date === dateStr);
    return day ? day.meals : [];
  };

  const getMealIcon = (type) => {
    return <Restaurant size={16} />;
  };

  const getConsumptionColor = (percentage) => {
    if (percentage === 0) return '#da1e28';
    if (percentage < 50) return '#ff832b';
    if (percentage < 80) return '#f1c21b';
    return '#9fb746';
  };

  return (
    <PageContainer>
      {/* Header sa Nazad button-om i profilom */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <Button
          kind="ghost"
          size="sm"
          renderIcon={ArrowLeft}
          hasIconOnly
          iconDescription="Nazad"
          onClick={() => navigate(`/residents/${id}`)}
        />
        
        <div 
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.75rem',
            padding: '0.5rem 0.75rem',
            backgroundColor: isDarkMode ? '#2d2d2d' : 'white',
            borderRadius: '4px',
            border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onClick={() => navigate(`/residents/${id}`)}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '4px',
            overflow: 'hidden',
            flexShrink: 0,
            backgroundColor: 'rgba(159, 183, 70, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <UserAvatar size={20} style={{ color: '#9fb746' }} />
          </div>
          <div>
            <div style={{ 
              fontSize: '0.875rem', 
              fontWeight: '600',
              color: isDarkMode ? '#f4f4f4' : 'inherit'
            }}>
              {resident?.firstName || 'Štićenik'} {resident?.lastName || ''}
            </div>
            <div style={{ 
              fontSize: '0.7rem', 
              opacity: 0.7,
              color: isDarkMode ? '#d0d0d0' : 'inherit'
            }}>
              Soba {resident?.room || '-'}
            </div>
          </div>
        </div>
      </div>

      {/* Režim ishrane i alergeni - Hero Section */}
      <div style={{ 
        marginBottom: '2rem',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '1rem'
      }}>
        {/* Trenutni režim */}
        <Tile style={{ 
          padding: '1.5rem',
          background: isDarkMode 
            ? 'linear-gradient(135deg, rgba(15, 98, 254, 0.15) 0%, #2d2d2d 100%)'
            : 'linear-gradient(135deg, rgba(15, 98, 254, 0.1) 0%, white 100%)',
          border: '2px solid rgba(15, 98, 254, 0.3)',
          boxShadow: '0 4px 12px rgba(15, 98, 254, 0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              backgroundColor: 'rgba(15, 98, 254, 0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Restaurant size={28} style={{ color: '#0f62fe' }} />
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', opacity: 0.7, color: isDarkMode ? '#d0d0d0' : 'inherit' }}>Režim ishrane</div>
              <div style={{ fontSize: '1.25rem', fontWeight: '600', color: isDarkMode ? '#f4f4f4' : '#0f62fe' }}>
                {dietType || 'Standardna ishrana'}
              </div>
            </div>
          </div>
          <div style={{ 
            fontSize: '0.8125rem', 
            lineHeight: '1.5',
            opacity: 0.85,
            color: isDarkMode ? '#e8e8e8' : 'inherit'
          }}>
            {dietDescription || 'Nema opisa ishrane'}
          </div>
          
          {/* Restrikcije */}
          <div style={{ marginTop: '1rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {restrictions.map((restriction, idx) => (
              <Tag 
                key={idx}
                type={restriction.severity === 'high' ? 'red' : 'blue'}
                size="sm"
              >
                {restriction.type}
              </Tag>
            ))}
          </div>
        </Tile>

        {/* Alergeni */}
        <Tile style={{ 
          padding: '1.5rem',
          background: isDarkMode
            ? 'linear-gradient(135deg, rgba(218, 30, 40, 0.15) 0%, #2d2d2d 100%)'
            : 'linear-gradient(135deg, rgba(218, 30, 40, 0.1) 0%, white 100%)',
          border: '2px solid rgba(218, 30, 40, 0.3)',
          boxShadow: '0 4px 12px rgba(218, 30, 40, 0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              backgroundColor: 'rgba(218, 30, 40, 0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Warning size={28} style={{ color: '#da1e28' }} />
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', opacity: 0.7, color: isDarkMode ? '#d0d0d0' : 'inherit' }}>Upozorenje</div>
              <div style={{ fontSize: '1.25rem', fontWeight: '600', color: isDarkMode ? '#f4f4f4' : '#da1e28' }}>
                Alergeni
              </div>
            </div>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {allergens.map((allergen, idx) => (
              <div 
                key={idx}
                style={{
                  padding: '0.75rem',
                  backgroundColor: isDarkMode ? 'rgba(218, 30, 40, 0.1)' : 'rgba(218, 30, 40, 0.05)',
                  borderRadius: '8px',
                  border: '1px solid rgba(218, 30, 40, 0.2)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Wheat size={20} style={{ color: '#da1e28' }} />
                  <span style={{ 
                    fontWeight: '600', 
                    fontSize: '0.875rem',
                    color: isDarkMode ? '#f4f4f4' : 'inherit'
                  }}>
                    {allergen.name}
                  </span>
                </div>
                <Tag type={allergen.severity === 'high' ? 'red' : 'magenta'} size="sm">
                  {allergen.severity === 'high' ? 'Visok rizik' : 'Srednji rizik'}
                </Tag>
              </div>
            ))}
          </div>
        </Tile>
      </div>

      {/* Istorija obroka i Specijalne napomene - Side by Side */}
      <div style={{ 
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 400px), 1fr))',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        {/* Istorija obroka */}
        <Tile style={{ padding: '0', boxShadow: '0 2px 8px rgba(159, 183, 70, 0.15)' }}>
          <div 
            style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              padding: '0.75rem',
              borderBottom: expandedSections.mealHistory ? '1px solid rgba(128,128,128,0.1)' : 'none',
              cursor: 'pointer'
            }}
            onClick={() => toggleSection('mealHistory')}
          >
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1rem' }}>
              <CalendarHeatMap size={20} style={{ color: '#9fb746' }} />
              Istorija obroka
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {/* View mode toggle */}
              <div style={{ 
                display: 'flex', 
                backgroundColor: isDarkMode ? '#2d2d2d' : '#f4f4f4',
                borderRadius: '4px',
                padding: '2px',
                gap: '2px'
              }}>
                <Button
                  kind={viewMode === 'week' ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setViewMode('week');
                  }}
                >
                  Nedelja
                </Button>
                <Button
                  kind={viewMode === 'day' ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!dayViewDate) setDayViewDate(new Date());
                    setViewMode('day');
                  }}
                >
                  Dan
                </Button>
              </div>
              <Button
                kind="ghost"
                size="sm"
                hasIconOnly
                iconDescription={expandedSections.mealHistory ? "Zatvori" : "Otvori"}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleSection('mealHistory');
                }}
              >
                {expandedSections.mealHistory ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </Button>
            </div>
          </div>
          
          <div style={{
            maxHeight: expandedSections.mealHistory ? '3000px' : '0',
            overflow: 'hidden',
            transition: expandedSections.mealHistory 
              ? 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease-in-out' 
              : 'max-height 0.2s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.15s ease-in-out',
            opacity: expandedSections.mealHistory ? 1 : 0
          }}>
            <div style={{ padding: '0.75rem' }}>
              {/* Week navigation */}
              {viewMode === 'week' && (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  marginBottom: '1rem',
                  padding: '0.5rem',
                  backgroundColor: isDarkMode ? '#2d2d2d' : '#f4f4f4',
                  borderRadius: '8px'
                }}>
                  <Button
                    kind="ghost"
                    size="sm"
                    renderIcon={ChevronLeft}
                    hasIconOnly
                    iconDescription="Prethodna nedelja"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigateWeek(-1);
                    }}
                  />
                  <div style={{ 
                    fontSize: '0.875rem', 
                    fontWeight: '600',
                    color: isDarkMode ? '#f4f4f4' : '#9fb746'
                  }}>
                    {menuData?.weekStartDate && menuData?.weekEndDate
                      ? `${new Date(menuData.weekStartDate).toLocaleDateString('sr-Latn-RS', { day: 'numeric', month: 'short' })} - ${new Date(menuData.weekEndDate).toLocaleDateString('sr-Latn-RS', { day: 'numeric', month: 'short', year: 'numeric' })}`
                      : 'Tekuća nedelja'}
                  </div>
                  <Button
                    kind="ghost"
                    size="sm"
                    renderIcon={ChevronRight}
                    hasIconOnly
                    iconDescription="Sledeća nedelja"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigateWeek(1);
                    }}
                  />
                </div>
              )}

              {/* Day view navigation */}
              {viewMode === 'day' && (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  marginBottom: '1rem',
                  padding: '0.5rem',
                  backgroundColor: isDarkMode ? '#2d2d2d' : '#f4f4f4',
                  borderRadius: '8px'
                }}>
                  <Button
                    kind="ghost"
                    size="sm"
                    renderIcon={ChevronLeft}
                    hasIconOnly
                    iconDescription="Prethodni dan"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigateDay(-1);
                    }}
                  />
                  <div style={{ 
                    fontSize: '0.875rem', 
                    fontWeight: '600',
                    color: isDarkMode ? '#f4f4f4' : '#9fb746'
                  }}>
                    {getDayLabel(dayViewDate)}
                  </div>
                  <Button
                    kind="ghost"
                    size="sm"
                    renderIcon={ChevronRight}
                    hasIconOnly
                    iconDescription="Sledeći dan"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigateDay(1);
                    }}
                  />
                </div>
              )}

              {isLoading ? (
                /* Skeleton loading state */
                <div className="nutrition-meal-days">
                  {[0, 1, 2].map((dayIdx) => (
                    <div key={dayIdx} className="nutrition-day">
                      <SkeletonText width="40%" heading />
                      <div className="nutrition-meals">
                        {[0, 1, 2, 3].map((mealIdx) => (
                          <SkeletonPlaceholder key={mealIdx} className="nutrition-meal-card-skeleton" />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="nutrition-meal-days">
                  {(viewMode === 'week' ? mealHistory : 
                    (() => {
                      const meals = getDayMeals(dayViewDate);
                      return meals.length > 0 ? [{ date: formatDateKey(dayViewDate), day: getDayLabel(dayViewDate), meals }] : [];
                    })()
                  ).map((day, dayIdx) => (
                    <div key={dayIdx} className="nutrition-day">
                      {/* Datum header */}
                      <div className="nutrition-day-header">
                        <div className="nutrition-day-title" style={{ color: isDarkMode ? '#f4f4f4' : '#9fb746' }}>
                          {day.day}, {day.date}
                        </div>
                      </div>

                      {/* Obroci */}
                      <div className="nutrition-meals">
                        {day.meals.map((meal, mealIdx) => (
                          <div
                            key={mealIdx}
                            className="nutrition-meal-card"
                            style={{
                              borderColor: meal.consumed ? getConsumptionColor(meal.percentage) : (isDarkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)'),
                              backgroundColor: isDarkMode ? '#262626' : '#ffffff'
                            }}
                            onClick={() => openViewPanel(meal, day)}
                          >
                            {/* Progress bar */}
                            {meal.consumed && (
                              <div className="nutrition-meal-progress"
                                style={{
                                  width: `${meal.percentage}%`,
                                  backgroundColor: getConsumptionColor(meal.percentage)
                                }}
                              />
                            )}

                            <div className="nutrition-meal-icon">
                              {getMealIcon(meal.type)}
                            </div>

                            <div className="nutrition-meal-info">
                              <div className="nutrition-meal-type" style={{ color: isDarkMode ? '#f4f4f4' : '#161616' }}>{meal.type}</div>
                              <div className="nutrition-meal-time" style={{ color: isDarkMode ? '#d0d0d0' : '#525252' }}>{meal.time}</div>
                              <div className="nutrition-meal-items" style={{ color: isDarkMode ? '#e8e8e8' : '#161616' }}>{meal.items}</div>
                            </div>

                            <div className="nutrition-meal-status">
                              {meal.consumed ? (
                                <Tag type="green" size="sm">
                                  <Checkmark size={12} /> {meal.percentage}%
                                </Tag>
                              ) : (
                                <Tag type="gray" size="sm">
                                  <Close size={12} /> Nije
                                </Tag>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Tile>
      </div>

      {/* Specijalne napomene */}
      <div>
        <Tile style={{ padding: '0', boxShadow: '0 2px 8px rgba(159, 183, 70, 0.15)' }}>
          <div 
            style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              padding: '0.75rem',
              borderBottom: expandedSections.specialNotes ? '1px solid rgba(128,128,128,0.1)' : 'none',
              cursor: 'pointer'
            }}
            onClick={() => toggleSection('specialNotes')}
          >
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1rem' }}>
              <Information size={20} style={{ color: '#9fb746' }} />
              Specijalne napomene
            </h3>
            <Button
              kind="ghost"
              size="sm"
              hasIconOnly
              iconDescription={expandedSections.specialNotes ? "Zatvori" : "Otvori"}
              onClick={(e) => {
                e.stopPropagation();
                toggleSection('specialNotes');
              }}
            >
              {expandedSections.specialNotes ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </Button>
          </div>
          
          <div style={{
            maxHeight: expandedSections.specialNotes ? '2000px' : '0',
            overflow: 'hidden',
            transition: expandedSections.specialNotes 
              ? 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease-in-out' 
              : 'max-height 0.2s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.15s ease-in-out',
            opacity: expandedSections.specialNotes ? 1 : 0
          }}>
            <div style={{ padding: '0.75rem' }}>
              {/* Nova napomena */}
              <div style={{ 
                marginBottom: '1rem', 
                padding: '0.75rem', 
                backgroundColor: isDarkMode ? 'rgba(159, 183, 70, 0.1)' : 'rgba(159, 183, 70, 0.05)', 
                borderRadius: '4px',
                border: '1px solid rgba(159, 183, 70, 0.2)'
              }}>
                <TextArea
                  labelText="Dodaj napomenu"
                  placeholder="Unesite napomenu o ishrani..."
                  rows={2}
                />
                <Button size="sm" style={{ marginTop: '0.5rem' }}>
                  Sačuvaj
                </Button>
              </div>

              {/* Lista napomena */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {specialNotes.map((note) => (
                  <div
                    key={note.id}
                    style={{
                      padding: '1rem',
                      backgroundColor: isDarkMode ? '#262626' : 'white',
                      border: '1px solid rgba(159, 183, 70, 0.2)',
                      borderLeft: '4px solid #9fb746',
                      borderRadius: '4px'
                    }}
                  >
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      marginBottom: '0.5rem',
                      flexWrap: 'wrap',
                      gap: '0.5rem'
                    }}>
                      <div style={{ 
                        fontWeight: '600', 
                        fontSize: '0.8125rem',
                        color: isDarkMode ? '#f4f4f4' : 'inherit'
                      }}>
                        {note.author}
                      </div>
                      <div style={{ 
                        fontSize: '0.7rem', 
                        opacity: 0.6,
                        color: isDarkMode ? '#d0d0d0' : 'inherit'
                      }}>
                        {note.date} • {note.time}
                      </div>
                    </div>
                    <div style={{ 
                      fontSize: '0.8125rem', 
                      lineHeight: '1.5',
                      color: isDarkMode ? '#e8e8e8' : 'inherit'
                    }}>
                      {note.note}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Tile>
      </div>

      {/* View Details Side Panel */}
      <div className={`nutrition-side-panel ${isViewPanelOpen ? 'open' : ''}`}>
        <div className="side-panel-header">
          <h2>Detalji obroka</h2>
          <Button
            kind="ghost"
            size="sm"
            renderIcon={Close}
            hasIconOnly
            onClick={closeViewPanel}
          />
        </div>

        {viewingMeal && (
          <div className="side-panel-content" style={{ padding: '1.5rem' }}>
            {/* Tip obroka */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              marginBottom: '1.5rem'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                backgroundColor: 'rgba(159, 183, 70, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Restaurant size={28} style={{ color: '#9fb746' }} />
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', opacity: 0.7, color: isDarkMode ? '#d0d0d0' : 'inherit' }}>
                  Tip obroka
                </div>
                <div style={{ fontSize: '1.25rem', fontWeight: '600', color: isDarkMode ? '#f4f4f4' : '#9fb746' }}>
                  {viewingMeal.type}
                </div>
              </div>
            </div>

            {/* Osnovne informacije */}
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '1rem',
              marginBottom: '1.5rem'
            }}>
              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: '0.75rem', 
                  fontWeight: '600',
                  color: 'var(--cds-text-secondary)',
                  marginBottom: '0.25rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Vreme
                </label>
                <div style={{ fontSize: '1rem' }}>
                  {viewingMeal.time}
                </div>
              </div>

              {viewingMeal.day && (
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '0.75rem', 
                    fontWeight: '600',
                    color: 'var(--cds-text-secondary)',
                    marginBottom: '0.25rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Datum
                  </label>
                  <div style={{ fontSize: '1rem' }}>
                    {viewingMeal.day.day}, {viewingMeal.day.date}
                  </div>
                </div>
              )}

              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: '0.75rem', 
                  fontWeight: '600',
                  color: 'var(--cds-text-secondary)',
                  marginBottom: '0.25rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Sastojci / Obrok
                </label>
                <div style={{ fontSize: '1rem', lineHeight: '1.5' }}>
                  {viewingMeal.items}
                </div>
              </div>

              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: '0.75rem', 
                  fontWeight: '600',
                  color: 'var(--cds-text-secondary)',
                  marginBottom: '0.25rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Status
                </label>
                <div>
                  {viewingMeal.consumed ? (
                    <Tag type="green" size="md">
                      <Checkmark size={12} /> Konzumirano {viewingMeal.percentage}%
                    </Tag>
                  ) : (
                    <Tag type="gray" size="md">
                      <Close size={12} /> Nije konzumirano
                    </Tag>
                  )}
                </div>
              </div>
            </div>

            {/* Progress bar */}
            {viewingMeal.consumed && (
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ 
                  display: 'block', 
                  fontSize: '0.75rem', 
                  fontWeight: '600',
                  color: 'var(--cds-text-secondary)',
                  marginBottom: '0.5rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Procenat konzumacije
                </label>
                <div style={{
                  height: '8px',
                  backgroundColor: isDarkMode ? '#2d2d2d' : '#e0e0e0',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    height: '100%',
                    width: `${viewingMeal.percentage}%`,
                    backgroundColor: getConsumptionColor(viewingMeal.percentage),
                    borderRadius: '4px',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
                <div style={{ 
                  fontSize: '0.875rem', 
                  fontWeight: '600',
                  color: getConsumptionColor(viewingMeal.percentage),
                  marginTop: '0.5rem'
                }}>
                  {viewingMeal.percentage}%
                </div>
              </div>
            )}

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Button 
                kind="secondary" 
                size="md"
                style={{ flex: 1 }}
                onClick={closeViewPanel}
              >
                Zatvori
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Overlay for View Panel */}
      {isViewPanelOpen && (
        <div 
          className="side-panel-overlay" 
          onClick={closeViewPanel}
        />
      )}
    </PageContainer>
  );
};

export default ResidentNutrition;
